import React, { useState, useEffect, useRef } from 'react';

const OtterGame = () => {
  // ========== DIFFICULTY SETTINGS - ADJUST THESE ==========
  const DIFFICULTY_CONFIG = {
    baseSpawnInterval: 1200,      // Base time between spawns (lower = more objects)
    score100SpawnInterval: 800,   // Spawn interval at score 100 (50% more trash)
    score200SpawnInterval: 600,   // Spawn interval at score 200 (2x more trash)
    time60Multiplier: 0.5,        // Multiply spawn interval by this at 60 seconds (0.5 = 2x trash)
  };
  // ========================================================
  
  const canvasRef = useRef(null);
  const [score, setScore] = useState(50);
  const [time, setTime] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [finalTime, setFinalTime] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  
  const [images, setImages] = useState({
    background1: null,
    background2: null,
    otter: null,
    fish1: null,
    fish2: null,
    trash1: null,
    trash2: null,
    trash3: null,
    trash4: null,
    trash5: null,
    trash6: null,
    bubble: null
  });

  const gameStateRef = useRef({
    otterY: 0,
    velocity: 0,
    objects: [],
    lastSpawn: 0,
    backgroundOffset: 0,
    currentTime: 0
  });

  const otterFacts = [
    "Sea otters hold hands while sleeping to keep from drifting apart! 🦦",
    "Otters have the densest fur of any animal - up to 1 million hairs per square inch!",
    "Sea otters use rocks as tools to crack open shellfish - they're one of the few animals to use tools!",
    "A group of otters is called a 'raft' when they're in the water!",
    "Sea otters eat 25% of their body weight every day to stay warm!",
    "Baby otters can't swim at first - their fur is too buoyant and they float like corks!",
    "The ocean covers 71% of Earth's surface and contains 97% of Earth's water!",
    "More than 80% of the ocean has never been mapped or explored!",
    "Otters are a keystone species - they help maintain healthy kelp forests!",
    "Sea otters can live their entire lives without leaving the water!",
    "14 billion pound of garbage are dumped into the ocean every year, and that number will only increase if nothing is done.",
    "100,000 animals die every year from entangalment in trash.",
    "By 2050, the ocean will have more trash than fish by weight.",
    "the great garbage patch is twice the size of texas as of now.",
    "About 10% of Americas beaches fail to meet the federal benchmark for what constitutes safe swimming water.",
    "The Mississippi River carries an estimated 1.5 million metric tons of nitrogen pollution into the Gulf of Mexico each year.",
    "Over 5.25 Trillion Pieces of Plastic Trash Are in Our Oceans.",
    "Over 1 million seabirds and 100,000 sea mammals are killed by pollution every year.",
    "Approximately 40% of the lakes in America are too polluted for fishing, aquatic life, or swimming."
  ];

  // Load images from public folder on mount
  useEffect(() => {
    const imageKeys = ['background1', 'background2', 'otter', 'fish1', 'fish2', 'trash1', 'trash2', 'trash3', 'trash4', 'trash5', 'trash6', 'bubble'];
    const loadedImages = {};
    let loadCount = 0;

    imageKeys.forEach(key => {
      const img = new Image();
      img.onload = () => {
        loadedImages[key] = img;
        loadCount++;
        if (loadCount === imageKeys.length) {
          setImages(loadedImages);
          setImagesLoaded(true);
        }
      };
      img.onerror = () => {
        console.error(`Failed to load image: ${key}`);
        loadCount++;
        if (loadCount === imageKeys.length) {
          setImagesLoaded(true);
        }
      };
      img.src = `/images/${key}.png`;
    });
  }, []);

  useEffect(() => {
    if (score <= 0 && gameStarted && !gameOver) {
      setGameOver(true);
      setFinalTime(time);
    }
  }, [score, gameStarted, gameOver, time]);

  useEffect(() => {
    let interval;
    if (gameStarted && !gameOver && !paused) {
      interval = setInterval(() => {
        setTime(t => {
          const newTime = t + 0.01;
          gameStateRef.current.currentTime = newTime;
          return newTime;
        });
      }, 10);
    }
    return () => clearInterval(interval);
  }, [gameStarted, gameOver, paused]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imagesLoaded) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width = 800;
    const height = canvas.height = 600;
    
    gameStateRef.current.otterY = height / 2;
    
    let animationId;
    let startTime = null;

    const handleKeyDown = (e) => {
      // Restart works anytime
      if (e.key === 'r' || e.key === 'R') {
        setScore(50);
        setTime(0);
        setGameStarted(false);
        setGameOver(false);
        setPaused(false);
        setFinalTime(0);
        setCurrentFactIndex(0);
        gameStateRef.current.otterY = height / 2;
        gameStateRef.current.velocity = 0;
        gameStateRef.current.objects = [];
        gameStateRef.current.lastSpawn = 0;
        gameStateRef.current.currentTime = 0;
        return;
      }
      
      if (gameOver) return;
      
      // Pause toggle
      if (e.key === 'p' || e.key === 'P') {
        if (gameStarted) {
          setPaused(p => !p);
        }
        return;
      }
      
      if (paused) return;
      
      if (e.key === 'q' || e.key === 'Q') {
        setGameOver(true);
        setFinalTime(time);
        return;
      }
      
      if (e.key === 'ArrowUp') {
        gameStateRef.current.velocity = -5;
      } else if (e.key === 'ArrowDown') {
        gameStateRef.current.velocity = 5;
      }
      if (!gameStarted && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        setGameStarted(true);
        startTime = performance.now();
      }
    };

    const handleKeyUp = (e) => {
      if (paused || gameOver) return;
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        gameStateRef.current.velocity = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const spawnObject = (timestamp) => {
      // Calculate spawn interval based on score
      let spawnInterval;
      if (score >= 200) {
        spawnInterval = DIFFICULTY_CONFIG.score200SpawnInterval;
      } else if (score >= 100) {
        // Interpolate between score100 and score200 intervals
        const progress = (score - 100) / 100;
        spawnInterval = DIFFICULTY_CONFIG.score100SpawnInterval - 
          (DIFFICULTY_CONFIG.score100SpawnInterval - DIFFICULTY_CONFIG.score200SpawnInterval) * progress;
      } else {
        // Interpolate between base and score100 intervals
        const progress = score / 100;
        spawnInterval = DIFFICULTY_CONFIG.baseSpawnInterval - 
          (DIFFICULTY_CONFIG.baseSpawnInterval - DIFFICULTY_CONFIG.score100SpawnInterval) * progress;
      }
      
      // Apply time-based multiplier at 60 seconds
      if (gameStateRef.current.currentTime >= 60) {
        spawnInterval *= DIFFICULTY_CONFIG.time60Multiplier;
      }
      
      if (timestamp - gameStateRef.current.lastSpawn > spawnInterval) {
        const rand = Math.random();
        let objectType;
        
        if (rand > 0.92) {
          objectType = 'food';
        } else if (rand > 0.84) {
          objectType = 'bubble';
        } else {
          objectType = 'trash';
        }
        
        const trashTypes = ['trash1', 'trash2', 'trash3', 'trash4', 'trash5', 'trash6'];
        const fishTypes = ['fish1', 'fish2'];
        
        let imageKey, size;
        if (objectType === 'food') {
          imageKey = fishTypes[Math.floor(Math.random() * 2)];
          size = 60;
        } else if (objectType === 'bubble') {
          imageKey = 'bubble';
          size = 50;
        } else {
          imageKey = trashTypes[Math.floor(Math.random() * 6)];
          size = 56.25;
        }
        
        gameStateRef.current.objects.push({
          x: width,
          y: Math.random() * (height - 150) + 50,
          speed: (2 + Math.random() * 3.3) * 1.05,
          type: objectType,
          size: size,
          imageKey: imageKey
        });
        gameStateRef.current.lastSpawn = timestamp;
      }
    };

    const checkCollision = (obj) => {
      const otterX = width / 3;
      const otterY = gameStateRef.current.otterY;
      const otterSize = 50;
      
      const dx = obj.x - otterX;
      const dy = obj.y - otterY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      return distance < (otterSize + obj.size) / 2.5;
    };

    const gameLoop = (timestamp) => {
      // Don't run game loop if paused
      if (paused) {
        animationId = requestAnimationFrame(gameLoop);
        return;
      }
      
      // Update background scroll
      if (gameStarted && !gameOver) {
        gameStateRef.current.backgroundOffset -= 1;
        if (gameStateRef.current.backgroundOffset <= -width) {
          gameStateRef.current.backgroundOffset = 0;
        }
      }
      
      // Draw background
      const currentBackground = gameStateRef.current.currentTime >= 60 ? images.background2 : images.background1;
      if (currentBackground) {
        ctx.drawImage(currentBackground, gameStateRef.current.backgroundOffset, 0, width, height);
        ctx.drawImage(currentBackground, gameStateRef.current.backgroundOffset + width, 0, width, height);
      } else {
        ctx.fillStyle = '#1e90ff';
        ctx.fillRect(0, 0, width, height);
      }

      if (gameStarted && !gameOver) {
        // Update otter position
        gameStateRef.current.otterY += gameStateRef.current.velocity;
        gameStateRef.current.otterY = Math.max(30, Math.min(height - 80, gameStateRef.current.otterY));

        // Spawn objects
        spawnObject(timestamp);

        // Update and draw objects
        gameStateRef.current.objects = gameStateRef.current.objects.filter(obj => {
          obj.x -= obj.speed;
          
          if (checkCollision(obj)) {
            if (obj.type === 'food') {
              setScore(s => s + 15);
            } else if (obj.type === 'bubble') {
              setCurrentFactIndex(Math.floor(Math.random() * otterFacts.length));
            } else {
              setScore(s => s - 10);
            }
            return false;
          }
          
          if (obj.x < -50) return false;
          
          // Draw object
          const img = images[obj.imageKey];
          if (img) {
            ctx.drawImage(img, obj.x - obj.size / 2, obj.y - obj.size / 2, obj.size, obj.size);
          }
          
          return true;
        });
      }

      // Draw otter
      const otterX = width / 3;
      const otterY = gameStateRef.current.otterY;
      
      if (images.otter) {
        ctx.drawImage(images.otter, otterX - 52.5, otterY - 37.5, 105, 75);
      } else {
        ctx.fillStyle = '#8B4513';
        ctx.beginPath();
        ctx.ellipse(otterX, otterY, 40, 25, 0, 0, Math.PI * 2);
        ctx.fill();
      }

      if (!gameStarted) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, height / 2 - 80, width, 160);
        ctx.fillStyle = 'white';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Press ↑ or ↓ to Start!', width / 2, height / 2 - 20);
        ctx.font = '18px Arial';
        ctx.fillText('P = Pause | R = Restart | Q = Quit', width / 2, height / 2 + 20);
        ctx.font = '16px Arial';
        ctx.fillText('Collect food, avoid trash, bubbles give facts!', width / 2, height / 2 + 50);
        
        // Draw key/legend in grid below center box
        const keyStartY = height / 2 + 100;
        const iconSize = 35;
        const itemsPerRow = 3;
        const columnWidth = 180;
        const rowHeight = 50;
        const gridStartX = width / 2 - (itemsPerRow * columnWidth) / 2;
        
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(gridStartX - 20, keyStartY - 20, itemsPerRow * columnWidth + 40, rowHeight * 3 + 40);
        
        ctx.fillStyle = 'white';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Item Key', width / 2, keyStartY);
        
        ctx.font = '14px Arial';
        ctx.textAlign = 'left';
        
        const items = [
          { img: images.fish1, name: 'Oyster' },
          { img: images.fish2, name: 'Urchin' },
          { img: images.bubble, name: 'Fact bubble' },
          { img: images.trash1, name: '6-can holder' },
          { img: images.trash2, name: 'Plastic bag' },
          { img: images.trash3, name: 'Bottle' },
          { img: images.trash4, name: 'Can' },
          { img: images.trash5, name: 'Net' }
          // { img: images.trash6, name: 'Trash 6' }
        ];
        
        items.forEach((item, index) => {
          if (item.img) {
            const row = Math.floor(index / itemsPerRow);
            const col = index % itemsPerRow;
            const x = gridStartX + col * columnWidth;
            const y = keyStartY + 30 + row * rowHeight;
            
            ctx.drawImage(item.img, x, y - iconSize/2, iconSize, iconSize);
            ctx.fillText(': ' + item.name, x + iconSize + 10, y + 5);
          }
        });
      }

      if (paused && gameStarted && !gameOver) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, height / 2 - 60, width, 120);
        ctx.fillStyle = 'white';
        ctx.font = 'bold 48px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('PAUSED', width / 2, height / 2);
        ctx.font = '20px Arial';
        ctx.fillText('Press P to Resume', width / 2, height / 2 + 40);
      }

      if (gameOver) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = 'white';
        ctx.font = 'bold 48px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('GAME OVER', width / 2, height / 2 - 40);
        ctx.font = '32px Arial';
        ctx.fillText(`Final Time: ${finalTime.toFixed(2)}s`, width / 2, height / 2 + 10);
        ctx.font = '24px Arial';
        ctx.fillText('Press R to Restart', width / 2, height / 2 + 60);
      }

      animationId = requestAnimationFrame(gameLoop);
    };

    animationId = requestAnimationFrame(gameLoop);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      cancelAnimationFrame(animationId);
    };
  }, [imagesLoaded, gameStarted, gameOver, finalTime, paused]);

  const currentFact = otterFacts[currentFactIndex];

  if (!imagesLoaded) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#1e3a8a',
        padding: '32px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: '32px'
        }}>
          <h1 style={{
            fontSize: '30px',
            fontWeight: 'bold',
            marginBottom: '16px',
            textAlign: 'center'
          }}>Loading Otter Game...</h1>
          <p style={{
            textAlign: 'center',
            color: '#4b5563'
          }}>Make sure images are in the /public/images/ folder</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#1e3a8a',
      padding: '16px'
    }}>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <canvas 
          ref={canvasRef} 
          style={{
            border: '4px solid #1d4ed8',
            borderRadius: '8px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            display: 'block',
            maxWidth: '100%',
            height: 'auto'
          }}
        />
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          fontWeight: 'bold',
          fontSize: '20px'
        }}>
          Score: {score}
        </div>
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          fontWeight: 'bold',
          fontSize: '20px'
        }}>
          Time: {time.toFixed(2)}s
        </div>
      </div>
      <div style={{
        width: '100%',
        maxWidth: '800px',
        backgroundColor: 'black',
        color: 'white',
        padding: '8px 16px',
        overflow: 'hidden',
        marginTop: '8px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          fontSize: '16px',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          {currentFact}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// NEWS CSV LOADER (Home Page)
// ============================================================
const NewsHome = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadNews = async () => {
      try {
        // Expects a CSV file at /news.csv with columns: title, date, description, link
        const response = await fetch('/news.csv');
        if (!response.ok) throw new Error('CSV not found');
        const text = await response.text();
        
        const lines = text.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const parsed = lines.slice(1).map(line => {
          // Simple CSV parse (handles basic comma-separated, not quoted commas)
          const values = line.split(',').map(v => v.trim());
          const obj = {};
          headers.forEach((h, i) => { obj[h] = values[i] || ''; });
          return obj;
        });
        
        setNews(parsed);
      } catch (err) {
        setError('Could not load news.csv. Place a CSV file at /public/news.csv with columns: title, date, description, link');
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, []);

  return (
    <div style={{ padding: '40px 20px', maxWidth: '1100px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '24px', color: '#1e3a8a' }}>Recent News</h1>
      
      {loading && <p style={{ color: '#6b7280' }}>Loading news...</p>}
      
      {error && (
        <div style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '16px', color: '#92400e' }}>
          {error}
        </div>
      )}
      
      {!loading && !error && news.length === 0 && (
        <p style={{ color: '#6b7280' }}>No news items found.</p>
      )}
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
        {news.map((item, i) => (
          <div key={i} style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>{item.date}</div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px', color: '#1e3a8a' }}>{item.title}</h3>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px', lineHeight: '1.5' }}>{item.description}</p>
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}>
                Read more →
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// SIMPLE LINK LIST PAGES
// ============================================================
const LinkListPage = ({ title, links }) => (
  <div style={{ padding: '40px 20px', maxWidth: '800px', margin: '0 auto' }}>
    <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '24px', color: '#1e3a8a' }}>{title}</h1>
    <ul style={{ listStyle: 'none', padding: 0 }}>
      {links.map((link, i) => (
        <li key={i} style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '16px 20px',
          marginBottom: '12px'
        }}>
          <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', fontWeight: '600', textDecoration: 'none', fontSize: '16px' }}>
            {link.label}
          </a>
          {link.description && <p style={{ margin: '6px 0 0 0', color: '#6b7280', fontSize: '14px' }}>{link.description}</p>}
        </li>
      ))}
    </ul>
    <p style={{ marginTop: '24px', color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>
      Programmer note: replace the placeholder links array in the code with real links for this page.
    </p>
  </div>
);

// ============================================================
// ABOUT PAGE
// ============================================================
const AboutPage = () => (
  <div style={{ padding: '40px 20px', maxWidth: '800px', margin: '0 auto' }}>
    <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '24px', color: '#1e3a8a' }}>About</h1>
    <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#374151', marginBottom: '16px' }}>
      This is placeholder About page text. Replace this paragraph with information about your organization,
      mission, and the purpose of this website. You might describe your goals around ocean conservation,
      education, or community engagement here.
    </p>
    <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#374151' }}>
      Add additional paragraphs, contact information, team bios, or history as needed. This text is
      easy to edit directly in the AboutPage component of the code.
    </p>
  </div>
);

// ============================================================
// NAVIGATION BAR
// ============================================================
const NavBar = ({ currentPage, setCurrentPage }) => {
  const [gamesOpen, setGamesOpen] = useState(false);

  const navItemStyle = (page) => ({
    padding: '10px 16px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
    color: currentPage === page ? '#1e3a8a' : '#374151',
    borderBottom: currentPage === page ? '2px solid #1e3a8a' : '2px solid transparent',
    whiteSpace: 'nowrap'
  });

  return (
    <nav style={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      backgroundColor: 'white',
      borderBottom: '1px solid #e5e7eb',
      padding: '0 20px',
      height: '60px',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={navItemStyle('home')} onClick={() => { setCurrentPage('home'); setGamesOpen(false); }}>
          Home
        </div>

        <div
          style={{ position: 'relative' }}
          onMouseEnter={() => setGamesOpen(true)}
          onMouseLeave={() => setGamesOpen(false)}
        >
          <div style={navItemStyle('game')} onClick={() => setGamesOpen(o => !o)}>
            Games ▾
          </div>
          {gamesOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              minWidth: '180px',
              overflow: 'hidden'
            }}>
              <div
                style={{ padding: '12px 16px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={() => { setCurrentPage('game'); setGamesOpen(false); }}
              >
                An Otter Game
              </div>
            </div>
          )}
        </div>

        <div style={navItemStyle('information')} onClick={() => { setCurrentPage('information'); setGamesOpen(false); }}>
          Information
        </div>
        <div style={navItemStyle('organizations')} onClick={() => { setCurrentPage('organizations'); setGamesOpen(false); }}>
          Organizations
        </div>
        <div style={navItemStyle('about')} onClick={() => { setCurrentPage('about'); setGamesOpen(false); }}>
          About
        </div>
      </div>
    </nav>
  );
};

// ============================================================
// MAIN APP
// ============================================================
const Website = () => {
  const [currentPage, setCurrentPage] = useState('home');

  // Placeholder links - programmer should replace with real URLs
  const informationLinks = [
    { label: 'Ocean Conservation Basics', url: '#', description: 'Placeholder link - replace with a real resource' },
    { label: 'Marine Life Guide', url: '#', description: 'Placeholder link - replace with a real resource' },
    { label: 'Plastic Pollution Facts', url: '#', description: 'Placeholder link - replace with a real resource' }
  ];

  const organizationLinks = [
    { label: 'Ocean Conservancy', url: '#', description: 'Placeholder link - replace with a real organization URL' },
    { label: 'Marine Mammal Center', url: '#', description: 'Placeholder link - replace with a real organization URL' },
    { label: 'Sea Otter Foundation & Trust', url: '#', description: 'Placeholder link - replace with a real organization URL' }
  ];

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <NavBar currentPage={currentPage} setCurrentPage={setCurrentPage} />
      
      {currentPage === 'home' && <NewsHome />}
      {currentPage === 'game' && <OtterGame />}
      {currentPage === 'information' && <LinkListPage title="Information" links={informationLinks} />}
      {currentPage === 'organizations' && <LinkListPage title="Organizations" links={organizationLinks} />}
      {currentPage === 'about' && <AboutPage />}
    </div>
  );
};

export default Website;
